import styled, { css } from "styled-components";
import { ReactNode } from "react";
import { screenSizes } from "../../styles/theme";

const buttonVariants = {
  primary: css`
    font-family: "Manrope", sans-serif;
    background-color: #ff9d59;
    color: #ffffff;
    border: none;
    &:hover {
      background-color: #ff9d59;
    }
  `,
  outlined: css`
    font-family: "Manrope", sans-serif;
    background-color: transparent;
    border: 2px solid #ff9d59;
    color: #ff9d59;
    &:hover {
      background-color: transparent;
    }
  `,
};

const BaseButton = styled.button<{
  variant?: "primary" | "outlined";
  fullWidth?: boolean; // Custom prop
  small?: boolean;
}>`
  ${(props) => (props.small ? "padding: 16px 20px;" : "padding: 8px 20px;")}
  cursor: pointer;
  font-weight: 700;
  font-size: 16px;
  line-height: 18px;
  display: inline-flex !important;
  justify-content: center;
  align-items: center;
  border-radius: 80px;
  @media (max-width: ${screenSizes.L}px) {
    width: 100%;
  }
  ${(props) => props.fullWidth && "width: 100%;"}
  ${(props) =>
    buttonVariants[props.variant || "outlined"]} // Default to 'outlined'
  &:hover {
    background-color:rgb(255, 245, 238);
  }
`;

const BaseButtonResponsive = styled.button<{
  variant?: "primary" | "outlined";
  fullWidth?: boolean; // Custom prop
  small?: boolean;
}>`
  padding: 5px 15px;
  cursor: pointer;
  font-weight: 700;
  font-size: 16px;
  line-height: 18px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  border-radius: 80px;
  ${(props) => props.fullWidth && "width: 100%;"}
  ${(props) =>
    buttonVariants[props.variant || "outlined"]}
`;

// Button Without Icon
type ButtonProps = {
  children: ReactNode;
  variant?: "primary" | "outlined";
  fullWidth?: boolean;
  type?: "button" | "submit" | "reset"; // Add type prop
  onClick?: () => void;
  disabled?: boolean;
};

export const Button: React.FC<ButtonProps> = ({
  children,
  type = "button", // Default to "button"
  ...props
}) => {
  return (
    <BaseButton {...props} type={type}>
      {children}
    </BaseButton>
  );
};

export const ButtonSmall: React.FC<ButtonProps> = ({
  children,
  type = "button", // Default to "button"
  ...props
}) => {
  return (
    <BaseButton {...props} type={type} small>
      {children}
    </BaseButton>
  );
};

// Button With Icon
const IconWrapper = styled.span`
  margin-left: 15px;
  display: flex;
  align-items: center;
  svg {
    width: 24px;
    height: 24px;
  }
`;

const IconWrapperResponsive = styled.span`
  display: flex;
  align-items: center;
  svg {
    width: 24px;
    height: 24px;
  }
`;

const IconWrapperSmall = styled.span`
  margin-left: 15px;
  display: flex;
  align-items: center;
  svg {
    width: 16px;
    height: 16px;
  }
`;

const FirstIconWrapper = styled.span`
  transform: rotate(180deg);
  margin-right: 15px;
  display: flex;
  align-items: center;
  svg {
    width: 16px;
    height: 16px;
  }
`;

type IconButtonProps = ButtonProps & {
  icon: ReactNode; // Icon is required for IconButton
};

export const IconButtonSmall: React.FC<IconButtonProps> = ({
  children,
  icon,
  type = "button", // Default to "button"
  ...props
}) => {
  return (
    <BaseButton {...props} type={type} small>
      {children}
      <IconWrapperSmall>{icon}</IconWrapperSmall>
    </BaseButton>
  );
};

export const IconButton: React.FC<IconButtonProps> = ({
  children,
  icon,
  type = "button", // Default to "button"
  ...props
}) => {
  return (
    <BaseButton {...props} type={type}>
      {children}
      <IconWrapper>{icon}</IconWrapper>
    </BaseButton>
  );
};

export const IconButtonResponsive: React.FC<IconButtonProps> = ({
  children,
  icon,
  type = "button", // Default to "button"
  ...props
}) => {
  return (
    <BaseButtonResponsive {...props} type={type}>
      {children}
      <IconWrapperResponsive>{icon}</IconWrapperResponsive>
    </BaseButtonResponsive>
  );
};

export const IconButtonIconFirst: React.FC<IconButtonProps> = ({
  children,
  icon,
  type = "button", // Default to "button"
  ...props
}) => {
  return (
    <BaseButton {...props} type={type}>
      <FirstIconWrapper>{icon}</FirstIconWrapper>
      {children}
    </BaseButton>
  );
};
